/* globals document MutationObserver */
import React, {
  isValidElement,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import { useInitializer } from '@ds/react-utils'
import { Global, css } from '@emotion/react'
import { getDsUiModalPortalId } from '../../../utilities/globalPortalIds'
import {
  DATA_ARIA_ACTIVE,
  addAriaHiddenAttributes,
  addInertAttributes,
  getPortalSiblings,
  removeAriaHiddenAttributes,
  removeInertAttributes,
} from '../../../utilities/portalUtils'
import { globalIds } from '../../../variables'
import type { ModalProps, ModalImperativeHandle } from '../Modal'

export interface ModalPortalProps {
  /**
   * The `Modal.Portal` component expects a single `Modal` as a child, which it will mount
   * to a container element (managing accessibility concerns as modals are displayed).
   */
  children: React.ReactElement<ModalProps>
  /**
   * @ignore @internal Subject to change. Do not use outside of this repository.
   * Adds `inert` to the Modal portal's siblings
   */
  inertSiblings?: boolean
  /**
   * The id of the container element (i.e. portal target) that the Modal.Portal component
   * should use for mounting its child Modal.
   *
   * If an element with the provided id cannot be found, one will be created and appended
   * to document.body for this purpose.
   */
  targetId?: string
  /**
   * @ignore @internal Subject to change. Do not use outside of this repository.
   * A function to call when the ModalPortal `innertHTML` is empty and `aria-hidden` / `inert` attributes are removed.
   * It is safe to return focus to a previously focused element if needed.
   */
  onEmpty?: () => void
}

const OLIVE_LEGACY_MODAL_ZINDEX = 799

const modalsContainerClass = 'olv-modal-portal'

const modalsContainerStyles = `
  .${modalsContainerClass} {
    position: relative;
    z-index: ${OLIVE_LEGACY_MODAL_ZINDEX};
  }
`

export function ModalPortal({
  children,
  inertSiblings = false,
  targetId = globalIds.DsUiModals,
  onEmpty,
}: ModalPortalProps) {
  const modalPortalRef = useRef<ModalImperativeHandle>(null)

  const [modalsContainer, setModalsContainer] = useState<HTMLElement | null>()
  const ariaHiddenSiblings = useRef<Element[]>([])

  const globalPortalId = useInitializer(getDsUiModalPortalId)

  const onEmptyCallback = useCallback(() => {
    onEmpty?.()
  }, [onEmpty])

  useEffect(() => {
    const portalId = globalPortalId || targetId

    // Manages modal dom element
    const EXISTING_MODALS_CONTAINER = document.getElementById(portalId)

    // Use existing container if present, else create one
    let portalTarget = EXISTING_MODALS_CONTAINER
    if (!portalTarget) {
      portalTarget = document.createElement('div')
      portalTarget.setAttribute('id', portalId)
      portalTarget.setAttribute('class', modalsContainerClass)

      document.body.appendChild(portalTarget)
    }

    portalTarget.setAttribute(DATA_ARIA_ACTIVE, 'true')
    setModalsContainer(portalTarget)

    return () => {
      // Clean up only if this instance of useEffect added the div to the dom
      if (!EXISTING_MODALS_CONTAINER) {
        if (portalTarget) {
          document.body.removeChild(portalTarget)
        }
        setModalsContainer(null)
      }
    }
  }, [targetId, globalPortalId])

  useEffect(() => {
    // Manages mutation observer, aria-hidden
    let portalTargetObserver: MutationObserver | null = null

    if (modalsContainer) {
      portalTargetObserver = new MutationObserver(
        (/* mutations - mutations[0].target seems to cause flicker */) => {
          removeAriaHiddenAttributes(ariaHiddenSiblings.current)
          removeInertAttributes(ariaHiddenSiblings.current)
          // Content is being rendered in the portal, add aria-hidden to modal portal siblings
          const portalSiblings = getPortalSiblings(modalsContainer)

          addAriaHiddenAttributes(portalSiblings)

          const modalCurrentRef = modalPortalRef.current
          if (inertSiblings || modalCurrentRef?.inertSiblings) {
            addInertAttributes(portalSiblings)
          }

          ariaHiddenSiblings.current = portalSiblings

          // When the Modal content is empty, we attempt to return focus to the previously focused element
          // The Modal portal itself will still remain on the page
          if (modalsContainer && modalsContainer.innerHTML === '') {
            if (modalCurrentRef && modalCurrentRef.returnFocus) {
              modalCurrentRef.returnFocus()
            }
            onEmptyCallback()
          }
        },
      )

      portalTargetObserver.observe(modalsContainer, {
        childList: true,
        subtree: false,
      })
    }

    return () => {
      if (portalTargetObserver) {
        portalTargetObserver.disconnect()
        removeAriaHiddenAttributes(ariaHiddenSiblings.current)
        removeInertAttributes(ariaHiddenSiblings.current)
        ariaHiddenSiblings.current = []
      }
    }
  }, [inertSiblings, modalsContainer, onEmptyCallback])

  return modalsContainer ? (
    <>
      <Global styles={css(modalsContainerStyles)} />
      {ReactDOM.createPortal(
        React.Children.map(children, (child) => {
          if (isValidElement(child)) {
            return React.cloneElement<ModalProps>(child, {
              _modalPortalChild: true,
              _modalPortalRef: !onEmpty ? modalPortalRef : undefined,
            })
          }
          return null
        }),
        modalsContainer,
      )}
    </>
  ) : null
}

ModalPortal.displayName = 'Modal.Portal'

ModalPortal.propTypes = {
  children: PropTypes.element.isRequired,
  inertSiblings: PropTypes.bool,
  onEmpty: PropTypes.func,
  targetId: PropTypes.string,
}

ModalPortal.defaultProps = {
  targetId: globalIds.DsUiModals,
}
